/**
 * Writing Page (formerly Standalone Quest Page)
 * Daily challenge with streak tracking, vocabulary source selection,
 * and gate progress for standalone (non-course-bound) writing sessions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTokens } from '../../contexts/TokenContext';
import {
  GateType,
  DailyStatus,
  StreakInfo,
  VocabularySourcesResponse,
  VocabularySourceOption,
  StandaloneQuestProgress,
  GATE_CONFIGS,
} from '../../types/writing';
import {
  getDailyStatus,
  getStreakInfo,
  getVocabularySources,
  startStandaloneQuest,
  getStandaloneProgress,
  isGateAccessible,
  getTotalTokenCost,
} from '../../services/writingService';
import './CourseWritingPage.css'; // Use the same CSS as CourseWritingPage
import './WritingPage.css'; // Additional standalone-specific styles

const LTokenIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2"
    aria-hidden="true"
    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 2 }}
  >
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="7" strokeOpacity="0.4" strokeWidth="1"/>
    <path d="M9 7v10h6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Gate type to i18n key mapping
const GATE_I18N_KEYS: Record<GateType, { name: string; desc: string }> = {
  translation: { name: 'translateToEnglish', desc: 'translateToEnglish' },
  combine: { name: 'sentenceCombining', desc: 'sentenceCombiningDesc' },
  expansion: { name: 'paragraphExpansion', desc: 'paragraphExpansionDesc' },
  essay: { name: 'essayWriting', desc: 'essayWritingDesc' },
};

const WritingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['writing', 'common']);
  const { isAuthenticated } = useAuth();
  const { stats: tokenStats, canAfford, refreshStats } = useTokens();

  const [dailyStatus, setDailyStatus] = useState<DailyStatus | null>(null);
  const [streakInfo, setStreakInfo] = useState<StreakInfo | null>(null);
  const [vocabSources, setVocabSources] = useState<VocabularySourcesResponse | null>(null);
  const [progress, setProgress] = useState<StandaloneQuestProgress | null>(null);
  const [selectedSource, setSelectedSource] = useState<string>('due');
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all data on mount
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [status, streak, sources] = await Promise.all([
        getDailyStatus(),
        getStreakInfo(),
        getVocabularySources(),
      ]);

      setDailyStatus(status);
      setStreakInfo(streak);
      setVocabSources(sources);

      // If there's an active writing session, load its progress
      if (status.hasActiveQuest) {
        try {
          const prog = await getStandaloneProgress();
          setProgress(prog);
        } catch {
          // Active session might be course-bound, not standalone
        }
      }

      // Pre-select recommended source
      const recommended = sources.sources.find(s => s.recommended);
      if (recommended) {
        setSelectedSource(recommended.id);
      }
    } catch (err: any) {
      setError(err.message || t('error', { ns: 'common' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, loadData]);

  // Start a new standalone writing session
  const handleStartQuest = async () => {
    if (starting) return;

    setStarting(true);
    setError(null);

    try {
      const dto: any = {
        vocabularySource: selectedSource,
      };
      if (selectedSource === 'course' && selectedCourseId) {
        dto.courseId = selectedCourseId;
      }

      const newProgress = await startStandaloneQuest(dto);
      setProgress(newProgress);
      await refreshStats();

      // Refresh daily status
      const status = await getDailyStatus();
      setDailyStatus(status);
    } catch (err: any) {
      setError(err.message || 'Failed to start writing session');
    } finally {
      setStarting(false);
    }
  };

  // Navigate to a gate
  const handleGateClick = (gateType: GateType) => {
    if (!progress) return;
    const status = progress.gateProgress[gateType];
    if (status === 'locked') return;
    navigate(`/writing/${gateType}`);
  };

  // Render gate card
  const renderGateCard = (gateType: GateType, index: number) => {
    const config = GATE_CONFIGS.find(g => g.type === gateType);
    if (!config) return null;

    const status = progress?.gateProgress[gateType] || 'locked';
    const score = progress?.scores[gateType];
    const accessible = progress ? isGateAccessible(gateType, progress as any) : false;
    const clickable = accessible || status === 'completed';

    return (
      <div
        key={gateType}
        className={`quest-gate quest-gate--${status} ${clickable ? 'quest-gate--accessible' : ''}`}
        onClick={() => clickable && handleGateClick(gateType)}
        role="button"
        tabIndex={clickable ? 0 : -1}
        onKeyPress={(e) => e.key === 'Enter' && clickable && handleGateClick(gateType)}
      >
        {index > 0 && <div className="quest-gate__connector" />}

        <div className="quest-gate__icon">
          {status === 'completed' ? '✅' : status === 'locked' ? '🔒' : config.icon}
        </div>

        <div className="quest-gate__content">
          <h3 className="quest-gate__name">
            {t(GATE_I18N_KEYS[config.type].name)}
            <span className="quest-gate__name-en">{config.name}</span>
          </h3>

          <p className="quest-gate__description">{t(GATE_I18N_KEYS[config.type].desc)}</p>

          <div className="quest-gate__meta">
            <span className="quest-gate__cost">
              <LTokenIcon size={14} />
              {config.tokenCost} {t('tokens', { ns: 'common' })}
            </span>
            <span className="quest-gate__reward">
              <span className="xp-icon">⭐</span>
              {config.xpReward} XP
            </span>
          </div>

          {score !== undefined && (
            <div className={`quest-gate__score quest-gate__score--${score >= 60 ? 'pass' : 'fail'}`}>
              {t('score')}: {score}%
            </div>
          )}
        </div>

        {status === 'available' && (
          <div className="quest-gate__action">
            <span className="quest-gate__play">{t('start')}</span>
          </div>
        )}

        {status === 'completed' && (
          <div className="quest-gate__action">
            <span className="quest-gate__view">{t('reviewResults')}</span>
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="quest-page quest-page--loading">
        <div className="quest-loading">
          <div className="quest-loading__spinner" />
          <p>{t('loading', { ns: 'common' })}</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="quest-page quest-page--auth">
        <div className="quest-auth">
          <h2>{t('loginRequired')}</h2>
          <p>{t('loginToStart')}</p>
          <Link to="/login" className="quest-auth__btn">
            {t('login', { ns: 'common' })}
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !progress) {
    return (
      <div className="quest-page quest-page--error">
        <div className="quest-error">
          <h3>{t('error', { ns: 'common' })}</h3>
          <p>{error}</p>
          <button onClick={loadData} className="quest-error__retry">
            {t('retry', { ns: 'common' })}
          </button>
        </div>
      </div>
    );
  }

  // Active writing session
  if (progress && !progress.completedAt) {
    const allGatesCompleted = Object.values(progress.gateProgress).every(
      s => s === 'completed'
    );

    return (
      <div className="quest-page">
        <header className="quest-header">
          <div className="quest-header__left">
            <span className="quest-header__daily-badge">{t('dailyChallenge')}</span>
          </div>
          <div className="quest-header__center">
            <h1 className="quest-header__title">{t('title')}</h1>
            <p className="quest-header__subtitle">{t('personalizedQuestDesc')}</p>
          </div>
          <Link to="/writing/history" className="quest-header__history">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            {t('historyRecord')}
          </Link>
        </header>

        {streakInfo && streakInfo.currentStreak > 0 && (
          <div className="streak-banner streak-banner--active">
            <span className="streak-banner__fire">
              {streakInfo.currentStreak >= 7 ? '🔥' : '✨'}
            </span>
            <span className="streak-banner__count">
              {t('streakDays', { count: streakInfo.currentStreak })}
            </span>
            {streakInfo.multiplier > 1 && (
              <span className="streak-banner__multiplier">
                ×{streakInfo.multiplier} {t('streakBonus')}
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="standalone-quest__error">
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="quest-journey">
          <div className="quest-journey__path">
            {(['translation', 'combine', 'expansion', 'essay'] as GateType[]).map((gate, index) =>
              renderGateCard(gate, index)
            )}
          </div>

          {allGatesCompleted && (
            <div className="quest-completion">
              <div className="quest-completion__trophy">🏆</div>
              <h2 className="quest-completion__title">{t('questComplete')}</h2>
              <p className="quest-completion__message">{t('congratulations')}</p>

              <div className="quest-completion__actions">
                <button
                  className="quest-completion__retry-btn"
                  onClick={() => {
                    setProgress(null);
                    loadData();
                  }}
                >
                  {t('startNewQuest')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No active writing session - show dashboard
  const totalCost = getTotalTokenCost();
  const canStart = canAfford(totalCost) && vocabSources?.hasEnoughVocabulary;

  return (
    <div className="quest-page quest-page--start">
      <div className="quest-start">
        <div className="quest-start__header">
          <h1 className="quest-start__title">
            {t('dailyChallenge')}
            <span className="quest-start__subtitle">{t('subtitle')}</span>
          </h1>
          <Link to="/writing/history" className="standalone-quest__history-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            {t('historyRecord')}
          </Link>
        </div>

        <div className="standalone-quest__cards">
          <div className="sq-card sq-card--streak">
            <div className="sq-card__icon">
              {streakInfo && streakInfo.currentStreak > 0 ? '🔥' : '💤'}
            </div>
            <div className="sq-card__body">
              <h3 className="sq-card__title">{t('streak')}</h3>
              <div className="sq-card__value">
                {streakInfo?.currentStreak || 0} {t('days', { ns: 'common' })}
              </div>
              <div className="sq-card__sub">
                {t('longestStreak')}: {streakInfo?.longestStreak || 0} {t('days', { ns: 'common' })}
              </div>
              {streakInfo && streakInfo.nextMilestone > 0 && streakInfo.currentStreak > 0 && (
                <div className="sq-card__milestone">
                  {t('nextMilestone')}: {streakInfo.daysToMilestone} {t('days', { ns: 'common' })}
                </div>
              )}
            </div>
          </div>

          <div className="sq-card sq-card--progress">
            <div className="sq-card__icon">📊</div>
            <div className="sq-card__body">
              <h3 className="sq-card__title">{t('todayProgress')}</h3>
              <div className="sq-card__value">
                {dailyStatus?.questsCompleted || 0} {t('questsCompleted')}
              </div>
              <div className="sq-card__sub">
                {dailyStatus?.totalXpToday || 0} {t('xpEarned')}
              </div>
              {dailyStatus && dailyStatus.streakBonusXp > 0 && (
                <div className="sq-card__bonus">
                  +{dailyStatus.streakBonusXp} {t('streakBonus')}
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="standalone-quest__error">
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="quest-start__journey">
          <div className="quest-start__gates">
            {GATE_CONFIGS.map((config, index) => (
              <div key={config.type} className="quest-preview-gate">
                {index > 0 && <div className="quest-preview-gate__line" />}
                <div className="quest-preview-gate__icon">{config.icon}</div>
                <div className="quest-preview-gate__info">
                  <span className="quest-preview-gate__name">{t(GATE_I18N_KEYS[config.type].name)}</span>
                  <span className="quest-preview-gate__cost">
                    <LTokenIcon size={12} /> {config.tokenCost} | ⭐ {config.xpReward}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="standalone-quest__source-section">
          <h2 className="standalone-quest__section-title">
            {t('selectVocabularySource')}
          </h2>

          {!vocabSources?.hasEnoughVocabulary && (
            <div className="standalone-quest__not-enough">
              <p>{t('notEnoughVocabulary')}</p>
            </div>
          )}

          <div className="sq-source-list">
            {vocabSources?.sources.map((source: VocabularySourceOption) => (
              <label
                key={source.id}
                className={`sq-source ${selectedSource === source.id ? 'sq-source--selected' : ''} ${source.count === 0 ? 'sq-source--disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="vocabSource"
                  value={source.id}
                  checked={selectedSource === source.id}
                  onChange={() => setSelectedSource(source.id)}
                  disabled={source.count === 0}
                />
                <div className="sq-source__content">
                  <div className="sq-source__header">
                    <span className="sq-source__name">{source.name}</span>
                    <span className="sq-source__count">{source.count}</span>
                  </div>
                  <span className="sq-source__desc">{source.description}</span>
                  {source.recommended && (
                    <span className="sq-source__badge">{t('recommended')}</span>
                  )}
                </div>
              </label>
            ))}

            {vocabSources?.courses && vocabSources.courses.length > 0 && (
              <label
                className={`sq-source ${selectedSource === 'course' ? 'sq-source--selected' : ''}`}
              >
                <input
                  type="radio"
                  name="vocabSource"
                  value="course"
                  checked={selectedSource === 'course'}
                  onChange={() => setSelectedSource('course')}
                />
                <div className="sq-source__content">
                  <div className="sq-source__header">
                    <span className="sq-source__name">{t('fromCourse')}</span>
                  </div>
                  {selectedSource === 'course' && (
                    <select
                      className="sq-source__course-select"
                      value={selectedCourseId}
                      onChange={e => setSelectedCourseId(e.target.value)}
                    >
                      <option value="">---</option>
                      {vocabSources.courses.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.count})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </label>
            )}
          </div>
        </div>

        <div className="quest-start__summary">
          <div className="quest-start__cost">
            <span className="quest-start__label">{t('totalCost')}:</span>
            <span className="quest-start__value">
              <LTokenIcon size={14} /> {totalCost} {t('tokens', { ns: 'common' })}
            </span>
          </div>
          <div className="quest-start__balance">
            <span className="quest-start__label">{t('yourBalance')}:</span>
            <span className={`quest-start__value ${canAfford(totalCost) ? '' : 'quest-start__value--low'}`}>
              <LTokenIcon size={14} /> {tokenStats?.balance || 0}
            </span>
          </div>
        </div>

        <button
          className={`quest-start__btn ${!canStart ? 'quest-start__btn--disabled' : ''}`}
          onClick={handleStartQuest}
          disabled={
            starting ||
            !canStart ||
            (selectedSource === 'course' && !selectedCourseId)
          }
        >
          {starting ? t('generatingQuestions') : canStart ? t('startJourney') : t('notEnoughTokens')}
        </button>

        {!canAfford(totalCost) && (
          <Link to="/tokens" className="quest-start__get-tokens">
            {t('getMoreTokens')}
          </Link>
        )}
      </div>
    </div>
  );
};

export default WritingPage;
